import { useEffect, useState } from "react";
import { Link, useRoute } from "wouter";
import { PhaseFrise } from "@/components/procedure/PhaseFrise";
import {
  enrolerPartie,
  getDossier,
  getParties,
  radierPartie,
} from "@/lib/apiProcedure";
import type {
  DossierProcedure,
  MembrePartie,
  RolePartie,
} from "@/types/procedure";

const ROLES: { value: RolePartie; label: string }[] = [
  { value: "DEMANDEUR", label: "Partie demanderesse" },
  { value: "AVOCAT_DEMANDEUR", label: "Avocat du demandeur" },
  { value: "DEFENDEUR", label: "Partie défenderesse" },
  { value: "AVOCAT_DEFENDEUR", label: "Avocat du défendeur" },
];

function roleLabel(role: string): string {
  return ROLES.find((r) => r.value === role)?.label ?? role;
}

function apiDetail(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? fallback
  );
}

/**
 * Écran greffe — enrôlement des parties et de leurs conseils.
 * Peuple `case_members` côté parties (demandeur/défendeur + avocats), pendant
 * complémentaire de la constitution du tribunal. Indispensable pour que le
 * conseil adverse accède au dossier et que son camp soit déterminé (L3).
 */
export default function EnrolementPage() {
  const [, params] = useRoute<{ id: string }>("/greffe/cases/:id/parties");
  const id = params?.id;

  const [dossier, setDossier] = useState<DossierProcedure | null>(null);
  const [membres, setMembres] = useState<MembrePartie[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<RolePartie>("AVOCAT_DEFENDEUR");

  const reload = async () => {
    if (!id) return;
    const [resDossier, resParties] = await Promise.all([
      getDossier(id),
      getParties(id),
    ]);
    setDossier(resDossier.data);
    setMembres(resParties.data.membres);
  };

  useEffect(() => {
    if (!id) return;
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEnroler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !userId.trim()) return;
    setBusy(true);
    setErreur(null);
    try {
      await enrolerPartie(id, { user_id: userId.trim(), member_role: role });
      setUserId("");
      await reload();
    } catch (err) {
      setErreur(apiDetail(err, "Échec de l'enrôlement."));
    } finally {
      setBusy(false);
    }
  };

  const handleRadier = async (memberUserId: string) => {
    if (!id || !confirm("Radier ce membre du dossier ?")) return;
    setBusy(true);
    setErreur(null);
    try {
      await radierPartie(id, memberUserId);
      await reload();
    } catch (err) {
      setErreur(apiDetail(err, "Radiation impossible."));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !dossier) return <div className="p-6">Chargement…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <PhaseFrise currentPhase={dossier.phase_procedurale} />

      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">
          Enrôlement des parties — {dossier.reference}
        </h1>
        <Link
          href={`/greffe/cases/${id}/tribunal`}
          className="text-sm text-indigo-600 hover:underline"
        >
          → Constitution du tribunal
        </Link>
      </div>

      <div className="bg-white p-6 rounded-lg shadow space-y-6">
        <p className="text-sm text-gray-500">
          Inscrivez les parties et leurs conseils (notamment l'avocat de la
          partie défenderesse). Les arbitres relèvent de l'affectation du
          tribunal, pas de cet écran.
        </p>

        <form
          onSubmit={handleEnroler}
          className="flex flex-wrap gap-3 p-4 bg-gray-50 rounded"
        >
          <input
            type="text"
            placeholder="Identifiant (sub) de la partie / du conseil"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
            className="flex-1 min-w-[220px] rounded border p-2"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as RolePartie)}
            className="rounded border p-2"
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={busy}
            className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Enrôler
          </button>
        </form>

        {erreur && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
            {erreur}
          </div>
        )}

        <table className="w-full text-left">
          <thead>
            <tr className="border-b text-sm text-gray-500">
              <th className="py-2">Membre</th>
              <th className="py-2">Rôle</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {membres.map((m) => (
              <tr key={m.id} className="border-b">
                <td className="py-2 font-mono text-xs">{m.user_id}</td>
                <td className="py-2 text-sm text-gray-700">
                  {roleLabel(m.member_role)}
                </td>
                <td className="py-2 text-right">
                  <button
                    onClick={() => handleRadier(m.user_id)}
                    disabled={busy}
                    className="text-red-500 hover:underline text-sm disabled:opacity-50"
                  >
                    Radier
                  </button>
                </td>
              </tr>
            ))}
            {membres.length === 0 && (
              <tr>
                <td colSpan={3} className="py-3 text-sm text-gray-400 italic">
                  Aucune partie enrôlée pour le moment.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
